// interpret.ts
//
// JS port of PHP interpret_roll_result_arr() (~560 lines, 13 helpers, 156
// branches). Renders the body content for damage / skill / contesting / DC /
// dx / complex rolls — the rolls_full dice strip with colored badges, ADV/DIS
// grouping, modifier badge, ADV/DIS trace dropdown, and gate-suppressed damage
// breakdown.
//
// Tuple shape on rolls_full:
//   [ die_roll, die_label, die_meta? ]
//
// Colors come from DAMAGETYPE_CATALOG. Falls back to substring match on
// die_label against item_name.

import type { Roll, RollsFullTuple, RollMeta } from './types';

// ====================================================================
// Helpers
// ====================================================================

function _esc(s: unknown): string {
    if (typeof escapeHtml === 'function') return escapeHtml(String(s ?? ''));
    const map: Record<string, string> = {
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    };
    return String(s ?? '').replace(/[&<>"']/g, m => map[m] ?? m);
}

function _icon(key: string, w?: number, h?: number): string {
    if (typeof iconMap !== 'function') return '';
    if (w == null && h == null) return iconMap(key);
    return iconMap(key, w || 25, h || w || 25);
}

function _ucwords(s: string): string {
    if (typeof ucwordsAndSpace === 'function') return ucwordsAndSpace(s);
    return String(s || '').replace(/_/g, ' ').split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function _isUuid(s: unknown): boolean {
    if (typeof checkIfValidUUID === 'function') return checkIfValidUUID(String(s));
    return typeof s === 'string' && /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/.test(s);
}

interface DamageTypeRow {
    item_id?: string;
    item_name?: string;
    hex_color_code?: string;
    [k: string]: unknown;
}

function _damageTypeById(id: string | null | undefined): DamageTypeRow | null {
    if (!id) return null;
    const cat = (typeof DAMAGETYPE_CATALOG !== 'undefined') ? DAMAGETYPE_CATALOG : null;
    if (!cat) return null;
    return (cat[id] as DamageTypeRow) || null;
}

function _damageTypesAll(): DamageTypeRow[] {
    const cat = (typeof DAMAGETYPE_CATALOG !== 'undefined') ? DAMAGETYPE_CATALOG : null;
    if (!cat) return [];
    const seen = new Set<string>();
    const out: DamageTypeRow[] = [];
    for (const k in cat) {
        const row = cat[k] as DamageTypeRow;
        if (!row || !row.item_id || seen.has(row.item_id)) continue;
        seen.add(row.item_id);
        out.push(row);
    }
    return out;
}

function _parseRollsFull(rf: unknown): RollsFullTuple[] | null {
    if (Array.isArray(rf)) return rf as RollsFullTuple[];
    if (typeof rf === 'string' && rf !== '') {
        try {
            const parsed = JSON.parse(rf);
            return Array.isArray(parsed) ? parsed as RollsFullTuple[] : null;
        } catch { return null; }
    }
    return null;
}
export { _parseRollsFull as parseRollsFull };

function _parseAdvDisTrace(raw: unknown): unknown[] | null {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string' && raw !== '') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : null;
        } catch { return null; }
    }
    return null;
}

function _hexOnly(s: unknown): string {
    return String(s || '').replace(/[^a-fA-F0-9]/g, '').toLowerCase();
}

// ====================================================================
// 13 helper functions — direct ports
// ====================================================================

const _GATE_REASON_HUMAN: Record<string, string> = {
    size_below: 'Size gate: creature too small for this rider.',
    size_above: 'Size gate: creature too large for this rider.',
    creature_type_only_miss: 'Creature type gate: only applies to listed types.',
    creature_type_excluded: 'Creature type gate: excluded type.',
    creature_sub_type_only_miss: 'Creature sub-type gate: only applies to listed sub-types.',
    creature_sub_type_excluded: 'Creature sub-type gate: excluded sub-type.',
    creature_lineage_only_miss: 'Creature lineage gate: only applies to listed lineages.',
    creature_lineage_excluded: 'Creature lineage gate: excluded lineage.',
    creature_ancestry_only_miss: 'Creature ancestry gate: only applies to listed ancestries.',
    creature_ancestry_excluded: 'Creature ancestry gate: excluded ancestry.',
    creature_grouping_only_miss: 'Creature grouping gate: only applies to listed groupings.',
    creature_grouping_excluded: 'Creature grouping gate: excluded grouping.',
    creature_classification_only_miss: 'Creature classification gate: only applies to the listed lineages / ancestries / groupings.',
    status_required_missing: 'Required status not on target for this damage fragment.',
    status_forbidden_present: 'Forbidden status active on target for this damage fragment.',
};

function _rollTupleMeta(t: unknown): RollMeta | null {
    if (!Array.isArray(t) || t.length < 3) return null;
    const m = t[2];
    if (m && typeof m === 'object' && !Array.isArray(m)) return m as RollMeta;
    if (Array.isArray(m)) return null;
    return null;
}

function _collectFirstDamageSourceHint(gateRows: unknown[]): string {
    for (const t of gateRows) {
        const m = _rollTupleMeta(t);
        if (!m) continue;
        const sn = String(m.source_name || '').trim();
        const so = String(m.source_origin_name || '').trim();
        const fg = String((m as Record<string, unknown>).ft_gate_source_name || '').trim();
        if (sn !== '') {
            return (so !== '' && so !== sn) ? `${sn} · ${so}` : sn;
        }
        if (fg !== '') return fg;
    }
    return '';
}

function _gateTooltipAppendSourceSuffix(m: RollMeta | null): string {
    if (!m) return '';
    const sn = String(m.source_name || '').trim();
    const so = String(m.source_origin_name || '').trim();
    const fg = String((m as Record<string, unknown>).ft_gate_source_name || '').trim();
    if (sn === '' && fg === '') return '';
    if (sn !== '') {
        const blob = sn + ((so !== '' && so !== sn) ? ` · ${so}` : '');
        return ` · Source: ${blob}`;
    }
    return ` · Source: ${fg}`;
}

function _gateMetaStatusItemIds(m: RollMeta | null): string[] {
    const arr = m ? (m as Record<string, unknown>).ft_gate_status_item_ids : null;
    if (!Array.isArray(arr)) return [];
    const out: string[] = [];
    for (const id of arr) {
        if (typeof id !== 'string' && typeof id !== 'number') continue;
        const s = String(id).trim();
        if (s !== '' && _isUuid(s)) out.push(s);
    }
    return Array.from(new Set(out));
}

function _gateMetaIconItemIds(m: RollMeta | null): string[] {
    const arr = m ? (m as Record<string, unknown>).ft_gate_icon_item_ids : null;
    if (!Array.isArray(arr)) return [];
    const out: string[] = [];
    for (const id of arr) {
        if (typeof id !== 'string' && typeof id !== 'number') continue;
        const s = String(id).trim();
        if (s !== '' && _isUuid(s)) out.push(s);
    }
    return Array.from(new Set(out));
}

function _gateMetaAllIconUuids(m: RollMeta | null): string[] {
    return Array.from(new Set([..._gateMetaIconItemIds(m), ..._gateMetaStatusItemIds(m)]));
}

function _renderInlineSizeGateRow(token: string): string {
    if (!token || !token.startsWith('ft_size_gate:')) return '';
    const parts = token.slice('ft_size_gate:'.length).split('|');
    if (parts.length !== 4) return '';
    const [casterSz, targetSz, anchorSz, cmp] = parts as [string, string, string, string];
    if (!_isUuid(casterSz) || !_isUuid(targetSz) || !_isUuid(anchorSz)) return '';
    const below = cmp === 'below';
    const r = 20, rk = 24;
    return `<div class="w-100 small mt-1 pt-1 border-top ft-log-gate-detail-size-strip">
        <div class="d-flex align-items-center flex-wrap gap-2">
            <span class="d-inline-flex align-items-center gap-1">${_icon('caster', r, r)} ${_icon(casterSz, rk, rk)}</span>
            <span class="opacity-50 user-select-none" aria-hidden="true">|</span>
            <span class="d-inline-flex align-items-center gap-1">${_icon('target', r, r)} ${_icon(targetSz, rk, rk)}</span>
        </div>
        <div class="d-inline-flex align-items-center flex-wrap gap-2 mt-1">
            ${_icon('creature_size', r, r)} ${_icon(anchorSz, rk, rk)}
            <span class="text-muted">${below ? 'or smaller.' : 'or larger.'}</span>
        </div>
    </div>`;
}

function _orderGateBreakdownRows(gateRows: unknown[]): unknown[] {
    const expl: unknown[] = [];
    const dice: unknown[] = [];
    for (const row of gateRows) {
        if (!Array.isArray(row)) continue;
        const m = _rollTupleMeta(row);
        if (m && (m as Record<string, unknown>).ft_gate_explanation) expl.push(row);
        else dice.push(row);
    }
    return [...expl, ...dice];
}

function _gateIconTooltipHint(gateRows: unknown[]): string {
    let body = '';
    for (const t of gateRows) {
        if (!Array.isArray(t) || t.length < 2) continue;
        const m = _rollTupleMeta(t);
        if (!m || !(m as Record<string, unknown>).ft_gate_explanation) continue;
        const labelEsc = _ucwords(typeof t[1] === 'string' || typeof t[1] === 'number' ? String(t[1]) : '');
        const rc = String((m as Record<string, unknown>).ft_gate_reason_code || '');
        if (rc !== '' && _GATE_REASON_HUMAN[rc]) {
            body = _GATE_REASON_HUMAN[rc] + ' — click to expand.';
            break;
        }
        if (labelEsc !== '') {
            body = labelEsc + ' — click to expand.';
            break;
        }
    }
    if (body === '') {
        const n = gateRows.length;
        body = n > 1
            ? `Dice did not apply on this fragment — click to expand (${n} lines).`
            : 'Dice did not apply on this fragment — click to expand.';
    }
    const src = _collectFirstDamageSourceHint(gateRows);
    return src !== '' ? `${src} — ${body}` : body;
}

function _partitionDamageGateSuppressed(rollsFullArr: RollsFullTuple[]): [RollsFullTuple[], RollsFullTuple[]] {
    const main: RollsFullTuple[] = [];
    const supp: RollsFullTuple[] = [];
    for (const t of rollsFullArr) {
        if (!Array.isArray(t) || t.length < 2) { main.push(t); continue; }
        const m = _rollTupleMeta(t);
        const mm = m as Record<string, unknown> | null;
        if (mm && (mm.ft_gate_not_applied || mm.ft_gate_explanation)) supp.push(t);
        else main.push(t);
    }
    return [main, supp];
}

function _renderGateSuppressedDamageBreakdown(roll: Roll, gateRowsIn: RollsFullTuple[]): string {
    if (!gateRowsIn || gateRowsIn.length === 0) return '';
    const gateRows = _orderGateBreakdownRows(gateRowsIn);
    const iconTooltip = _gateIconTooltipHint(gateRows);

    const traceRollI = Number(roll.roll_i || 0);
    const traceRid = _hexOnly(roll.roll_id || '');
    let gatePanelId: string;
    if (traceRollI > 0) {
        gatePanelId = 'ft_dmg_gate_' + traceRollI + (traceRid !== '' ? '_' + traceRid : '');
    } else if (traceRid !== '') {
        gatePanelId = 'ft_dmg_gate_' + traceRid + '_' + Math.random().toString(16).slice(2, 10);
    } else {
        gatePanelId = 'ft_dmg_gate_' + Math.random().toString(16).slice(2, 18);
    }

    let lastDamageSegmentDtId: string | null = null;
    let inner = '';

    for (const row of gateRows) {
        if (!Array.isArray(row) || row.length < 2) continue;
        const dieRoll = row[0];
        let dieRollLabel = row[1];
        if (typeof dieRollLabel !== 'string') {
            if (typeof dieRollLabel !== 'number' && typeof dieRollLabel !== 'boolean') continue;
            dieRollLabel = String(dieRollLabel);
        }
        if (dieRollLabel.includes('Temp V Mod')) continue;

        const dieMeta = _rollTupleMeta(row);
        const dmm = dieMeta as Record<string, unknown> | null;

        // Explanation row
        if (dmm && dmm.ft_gate_explanation) {
            const lineShort = _ucwords(dieRollLabel);
            const rc = String(dmm.ft_gate_reason_code || '');
            let tDetail = lineShort;
            if (rc !== '' && _GATE_REASON_HUMAN[rc]) tDetail = _GATE_REASON_HUMAN[rc];
            tDetail += _gateTooltipAppendSourceSuffix(dieMeta);
            const visualUuids = _gateMetaAllIconUuids(dieMeta);
            const sizeTok = String(dmm.ft_gate_size_gate_token || '').trim();

            const d0 = _esc(typeof dieRoll === 'string' || typeof dieRoll === 'number' ? String(dieRoll) : '');
            const iconsHtml = visualUuids.map(id =>
                `<span class="d-inline-flex align-items-center">${_icon(id, 22, 22)}</span>`
            ).join('');
            const sizeHtml = sizeTok ? _renderInlineSizeGateRow(sizeTok) : '';

            inner += `<div class="w-100">
                <div class="d-flex flex-wrap align-items-center gap-1">
                    <span class="badge bg-secondary text-wrap text-start d-inline-block fw-normal px-2 py-1 gate-explanation-chip align-middle"
                        style="white-space:normal; max-width:100%;"
                        data-bs-toggle="tooltip" data-bs-placement="top"
                        data-bs-original-title="${_esc(tDetail)}">
                        <span class="me-1 opacity-75">${d0}</span>${_esc(lineShort)}
                    </span>
                    ${iconsHtml}
                </div>
                ${sizeHtml}
            </div>`;
            continue;
        }

        // Plain dice that were rolled but suppressed
        let bg = 'bg-primary';
        let hex: string | null = null;

        const isAdv = dieRollLabel.includes('ADV');
        const isDis = dieRollLabel.includes('DIS');
        if (isAdv) bg = 'bg-success';
        else if (isDis) bg = 'bg-danger';

        let resolved: DamageTypeRow | null = null;
        if (dieMeta && dieMeta.damage_type_id) {
            const dtRow = _damageTypeById(dieMeta.damage_type_id);
            if (dtRow && dtRow.item_name) resolved = dtRow;
        }
        if (resolved) {
            hex = resolved.hex_color_code ?? null;
            bg = (resolved.item_name === 'Radiant') ? 'text-dark' : '';
        } else {
            for (const dt of _damageTypesAll()) {
                if (!dt.item_name || !dieRollLabel.includes(dt.item_name)) continue;
                hex = dt.hex_color_code ?? null;
                bg = (dt.item_name === 'Radiant') ? 'text-dark' : '';
                break;
            }
        }

        let starPrefix = '';
        if (typeof dieRoll === 'string' && ['0.5 Ω', '2 λ', 'θ'].indexOf(dieRoll) >= 0) {
            bg = 'bg-dark text-warning border';
            starPrefix = ' * ';
        }

        let tooltip = _ucwords(dieRollLabel) + _gateTooltipAppendSourceSuffix(dieMeta);
        const rc = dmm ? String(dmm.ft_gate_reason_code || '') : '';
        if (rc !== '' && _GATE_REASON_HUMAN[rc]) tooltip += ` · ${_GATE_REASON_HUMAN[rc]}`;
        else tooltip += ' · Rolled; not counted on this target.';

        let primarySepHtml = '';
        if (dieMeta && dieMeta.damage_type_id) {
            const kindStr = String(dieMeta.kind || '');
            if (kindStr === '') {
                const segId = String(dieMeta.damage_type_id);
                if (lastDamageSegmentDtId !== null && lastDamageSegmentDtId !== segId) {
                    primarySepHtml = ' <span class="text-muted fw-light mx-2">+</span> ';
                }
                lastDamageSegmentDtId = segId;
            }
        }

        const styleAttr = hex ? ` style="background-color:${_esc(hex)}"` : '';
        inner += `${starPrefix}${primarySepHtml}<span role="button" class="badge ${bg} d-inline-block border border-warning border-opacity-50"${styleAttr}
            data-bs-toggle="tooltip" data-bs-placement="top"
            data-bs-original-title="${_esc(tooltip)}">${_esc(dieRoll)}</span> `;
    }

    return `<span role="button" class="d-inline-block ms-1 ft-gate-breakdown-toggle"
            style="vertical-align:middle; cursor:pointer;"
            onclick='var el=document.getElementById("${_esc(gatePanelId)}"); if(el) el.style.display = el.style.display==="none"?"block":"none";'
            data-bs-toggle="tooltip" data-bs-placement="top"
            data-bs-original-title="${_esc(iconTooltip)}">${_icon('dice', 18, 18)}</span>
        <div id="${_esc(gatePanelId)}" style="display:none;" class="mt-1 gate-suppressed-dice-breakdown">
            <span class="visually-hidden">Part of this damage did not apply; expand for rationale and dice.</span>
            <div class="d-flex flex-wrap align-items-center gap-1 py-1 px-2" style="border:1px dashed rgba(255,193,7,.55); border-radius:.375rem; background:rgba(0,0,0,.08);">
                ${inner}
            </div>
        </div>`;
}

// ====================================================================
// Public entry: interpretRollResultArr
// ====================================================================

export function interpretRollResultArr(roll: Roll, campaignPermission: number): string {
    if (!roll) return '';
    let rollsFullArr = _parseRollsFull(roll.rolls_full);
    if (!Array.isArray(rollsFullArr)) return '';

    let suppressed: RollsFullTuple[] = [];
    const damageRollId = (typeof ROLLTYPE_CATALOG !== 'undefined' && ROLLTYPE_CATALOG.damage_roll)
        ? String(ROLLTYPE_CATALOG.damage_roll.item_id || '')
        : '';
    if (damageRollId !== '' && String(roll.roll_type_1 || '') === damageRollId) {
        const parts = _partitionDamageGateSuppressed(rollsFullArr);
        rollsFullArr = parts[0];
        suppressed = parts[1];
    }

    // Private + no permission → hidden_roll icon
    if (Number(roll.roll_private || 0) > 0 && Number(campaignPermission || 0) === 0) {
        return `<div class="row m-0">
            <div class="col-12 text-left">
                <span role="button" onclick="openItemInfo('6DB98121-B8AB-9F10-3A89-9A6EB2B3A49A');">
                    ${_icon('hidden_roll')}
                </span>
            </div>
        </div>`;
    }

    const advTraceRaw = _parseAdvDisTrace(roll.adv_dis_trace_json);

    let inner = '';
    let usedSrPlus = 0;
    let showMod = true;
    let lastBaseDie: string | null = null;
    let lastAdvSourceId: string | null = null;
    let inAdvGroup = false;
    let advPairPos = 0;
    let advPairSize = 2;
    let lastDamageSegmentDtId: string | null = null;

    const _closeAdvGroup = () => {
        if (!inAdvGroup) return;
        inner += `<span class='text-muted fw-light'>)</span> `;
        inAdvGroup = false;
        advPairPos = 0;
    };

    for (const tuple of rollsFullArr) {
        if (!Array.isArray(tuple) || tuple.length < 2) continue;
        const dieRoll = tuple[0];
        let dieRollLabel = tuple[1];
        if (typeof dieRollLabel !== 'string') {
            if (typeof dieRollLabel !== 'number' && typeof dieRollLabel !== 'boolean') continue;
            dieRollLabel = String(dieRollLabel);
        }

        let dieMeta: RollMeta | null = null;
        if (tuple.length >= 3 && tuple[2] && typeof tuple[2] === 'object' && !Array.isArray(tuple[2])) {
            dieMeta = tuple[2] as RollMeta;
        }

        let dieRollBg = 'bg-primary';
        let rollHexColor: string | null = null;

        if (dieRollLabel.includes('Temp V Mod')) continue;

        const baseDie = dieRollLabel.replace(/\s*(ADV|DIS|CRIT|KEPT).*$/i, '').trim();

        const isAdv = dieRollLabel.includes('ADV');
        const isDis = dieRollLabel.includes('DIS');

        // Parse stack count: "d6 ADV1" → 1; pair size = stack + 1.
        let dieStackCount = 1;
        if (isAdv || isDis) {
            const m = dieRollLabel.match(/(?:ADV|DIS)\s*(\d+)/i);
            if (m) {
                const n = parseInt(m[1]!, 10);
                if (Number.isFinite(n) && n > 0) dieStackCount = n;
            }
        }

        const advSourceId = (isAdv || isDis) && dieMeta
            ? String((dieMeta as Record<string, unknown>).source_item_id || '')
            : '';

        // Close the open group when the die type OR the source fragment changes.
        if (
            inAdvGroup
            && (
                (baseDie !== lastBaseDie && lastBaseDie !== null)
                || (advSourceId !== '' && lastAdvSourceId !== null && advSourceId !== lastAdvSourceId)
            )
        ) {
            _closeAdvGroup();
        }

        if (isAdv || isDis) {
            if (!inAdvGroup) {
                inner += `<span class='text-muted fw-light'>(</span> `;
                inAdvGroup = true;
                advPairPos = 0;
                advPairSize = dieStackCount + 1;
                dieRollBg = isAdv ? 'bg-success' : 'bg-danger';
            } else if (advPairPos === 0) {
                dieRollBg = isAdv ? 'bg-success' : 'bg-danger';
            } else {
                dieRollBg = 'bg-light text-dark';
            }

            // Number each die within the ADV/DIS group so hovering the dice of a pair
            // reads "D20 ADV 1" / "D20 ADV 2" (or DIS 1 / DIS 2) — distinct per die —
            // instead of every die repeating the stack count ("D20 ADV1").
            //
            // The number is the die's ROLL ORDER (1st rolled, 2nd rolled, …), NOT its
            // highest→lowest display position: roll_dice_arr value-sorts each pair so the
            // kept die renders first (green highlight), but tags every die with
            // meta.roll_order before sorting. Prefer that tag; fall back to the display
            // position (advPairPos, 0-based — set to 0 on group-open, ++'d after each die)
            // for legacy rolls that predate the meta.
            const advKind = isAdv ? 'ADV' : 'DIS';
            const advBaseLabel = _ucwords(baseDie);
            let advOrdinal = advPairPos + 1;
            const rollOrderRaw = dieMeta ? (dieMeta as Record<string, unknown>).roll_order : undefined;
            if (rollOrderRaw !== undefined && rollOrderRaw !== null) {
                const ro = parseInt(String(rollOrderRaw), 10);
                if (Number.isFinite(ro) && ro > 0) advOrdinal = ro;
            }
            let advTooltip = (advBaseLabel !== '' ? `${advBaseLabel} ` : '') + `${advKind} ${advOrdinal}`;
            if (dieMeta && dieMeta.source_name) {
                advTooltip += ` · ${dieMeta.source_name}`;
            }

            // Semantic hooks for the compact FT3D log. That surface projects this already-authorized
            // markup, so preserve the facts the full log knows instead of making it infer ADV/DIS from
            // colours or values. roll_dice_arr sorts the selected die first for both ADV and DIS.
            const advTone = isAdv ? 'adv' : 'dis';
            const advKept = advPairPos === 0 ? '1' : '0';

            inner += `<span role="button" class="badge ${dieRollBg} d-inline-block"
                data-ft-adv-dis-kind="${advTone}" data-ft-adv-dis-kept="${advKept}"
                data-bs-toggle="tooltip" data-bs-placement="top"
                data-bs-original-title="${_esc(advTooltip)}">
                ${_esc(dieRoll)}
            </span> `;

            advPairPos++;
            lastAdvSourceId = advSourceId;
            if (advPairPos >= advPairSize) {
                _closeAdvGroup();
            }
        } else {
            if (inAdvGroup) {
                _closeAdvGroup();
                lastAdvSourceId = null;
            }

            // ==================== STYLING BRANCHES (kept in lockstep with PHP) ====================
            if (String(dieRoll) === '20' && dieRollLabel === 'd20_1') dieRollBg = 'bg-success';
            else if (String(dieRoll) === '1' && dieRollLabel === 'd20_1') dieRollBg = 'bg-danger';

            if (dieRollLabel.includes('skill_check_min_')) {
                dieRollBg = 'bg-secondary';
                inner += ' → ';
            }

            if (dieRollLabel.includes('Extra Healing Roll')) {
                dieRollBg = 'bg-success';
                inner += ' + ';
                showMod = false;
            }
            if (dieRollLabel.includes('Healing Mod')) {
                dieRollBg = 'bg-warning';
                inner += ' + ';
                showMod = false;
            }

            if (dieRollLabel.includes('Reroll DT 1 or 2: ')) {
                dieRollBg = 'bg-warning';
                showMod = false;
            }
            if (dieRollLabel.includes('Reroll HR 1 or 2: ')) {
                dieRollBg = 'bg-warning';
                showMod = false;
            }

            if (dieRollLabel.includes('Skill Roll ')) {
                dieRollBg = 'bg-info';
                showMod = false;
                if (usedSrPlus === 0) inner += ' + ';
                usedSrPlus = 1;
            }

            if (dieRollLabel.includes('Skill Roll Mod') || dieRollLabel.includes('Skill Rolls Mod')) {
                dieRollBg = 'bg-secondary';
                showMod = false;
                inner += ' + ';
            }

            if (
                dieRollLabel.includes('Double Handed Strength')
                || (
                    dieRollLabel.includes('Mod')
                    && !dieRollLabel.includes('Healing Mod')
                    && !dieRollLabel.includes('Skill Roll Mod')
                    && !dieRollLabel.includes('Skill Rolls Mod')
                )
            ) {
                dieRollBg = 'bg-warning';
                inner += ' + ';
                showMod = false;
            }

            // Damage type resolution — meta first, then substring fallback
            let resolved: DamageTypeRow | null = null;
            if (dieMeta && dieMeta.damage_type_id) {
                const dtRow = _damageTypeById(dieMeta.damage_type_id);
                if (dtRow && dtRow.item_name) resolved = dtRow;
            }
            if (resolved) {
                rollHexColor = resolved.hex_color_code ?? null;
                dieRollBg = (resolved.item_name === 'Radiant') ? 'text-dark' : '';
                showMod = false;
            } else {
                for (const dt of _damageTypesAll()) {
                    if (!dt.item_name || !dieRollLabel.includes(dt.item_name)) continue;
                    rollHexColor = dt.hex_color_code ?? null;
                    dieRollBg = (dt.item_name === 'Radiant') ? 'text-dark' : '';
                    showMod = false;
                    break;
                }
            }

            // Essence rolls (Body/Mind/Soul) — hardcoded hex
            if (
                ['Body Essence', 'Mind Essence', 'Soul Essence'].indexOf(dieRollLabel) >= 0
                || /essence/i.test(dieRollLabel)
            ) {
                rollHexColor = /body/i.test(dieRollLabel) ? '#FF5733'
                             : /mind/i.test(dieRollLabel) ? '#3498DB'
                             : '#8E44AD';
                dieRollBg = '';
                inner += ' + ';
                showMod = false;
            }

            if (typeof dieRoll === 'string' && ['0.5 Ω', '2 λ', 'θ'].indexOf(dieRoll) >= 0) {
                dieRollBg = 'bg-dark text-warning border';
                showMod = false;
                inner += ' * ';
            }

            if (dieRollLabel.includes('All Damage Output Changed by Percentage')) {
                dieRollBg = 'bg-success text-warning border';
                showMod = false;
            }
            if (dieRollLabel.includes('All Incoming Damage Changed by Percentage')) {
                dieRollBg = 'bg-warning text-warning border';
                showMod = false;
            }

            if (dieRollLabel.includes('Critical Damage')) {
                dieRollBg = 'critical-damage';
            }

            // Exploding dice (campaign house rule). Matched on the structured meta kind rather than
            // the label, because unlike a crit an exploded die's own FACE VALUE carries no signal —
            // at Fortune 3+ the chain continues on max-1, max-2, … so the trigger die is often not a
            // max at all and nothing about the number identifies it. Gold glow, deliberately cooler
            // than the crit orange so a crit that also explodes reads as two distinct effects.
            if (dieMeta && String(dieMeta.kind || '') === 'explosion') {
                dieRollBg = 'exploding-die';
            }

            if (dieRollLabel === 'Damage Reduction' || dieRollLabel === 'DR') {
                dieRollBg = 'bg-secondary text-white border';
                showMod = false;
            }
            if (dieRollLabel === 'Damage Mitigation' || dieRollLabel === 'Object Damage Mitigation') {
                dieRollBg = 'bg-dark text-white border';
                showMod = false;
            }

            // Build structured tooltip if meta present
            let tooltip = _ucwords(dieRollLabel);
            if (dieMeta) {
                const originalDieForTooltip = _ucwords(baseDie);
                const metaKind = String(dieMeta.kind || '');
                const metaDtId = String(dieMeta.damage_type_id || '');
                const metaSrcNm = String(dieMeta.source_name || '').trim();
                const metaOrigin = String(dieMeta.source_origin_name || '').trim();
                let metaDtName = '';
                if (metaDtId !== '') {
                    const dtRow = _damageTypeById(metaDtId);
                    if (dtRow) metaDtName = String(dtRow.item_name || '');
                }

                switch (metaKind) {
                    case 'resistance': {
                        let t = metaDtName !== '' ? `${metaDtName} Resisted` : 'Resisted';
                        if (metaSrcNm !== '') t += ` · from ${metaSrcNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'immunity': {
                        let t = metaDtName !== '' ? `${metaDtName} Negated` : 'Negated';
                        if (metaSrcNm !== '') t += ` · from ${metaSrcNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'vulnerability': {
                        let t = metaDtName !== '' ? `${metaDtName} Vulnerability` : 'Vulnerability';
                        if (metaSrcNm !== '') t += ` · from ${metaSrcNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'resistance_ignored': {
                        let t = metaDtName !== '' ? `${metaDtName} Resistance Ignored` : 'Resistance Ignored';
                        if (metaSrcNm !== '') t += ` · would have been from ${metaSrcNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'immunity_ignored': {
                        let t = metaDtName !== '' ? `${metaDtName} Immunity Ignored` : 'Immunity Ignored';
                        if (metaSrcNm !== '') t += ` · would have been from ${metaSrcNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'damage_transfer': {
                        const dmrec = dieMeta as Record<string, unknown>;
                        const pct = dmrec.transfer_percentage;
                        const statusNm = String(dmrec.status_item_name || '').trim();
                        let t = 'Damage Transfer';
                        if (pct !== undefined && pct !== null) {
                            t += ' · ' + (typeof pct === 'number' ? `${Math.floor(pct * 100)}%` : String(pct));
                        }
                        if (statusNm !== '') t += ` · via ${statusNm}`;
                        tooltip = t;
                        break;
                    }
                    case 'essence':
                        break;
                    case 'critical': {
                        let t = _ucwords(dieRollLabel);
                        if (metaSrcNm !== '') t += ` · ${metaSrcNm}`;
                        if (originalDieForTooltip !== '') t = `${originalDieForTooltip} · ${t}`;
                        tooltip = t;
                        break;
                    }
                    case 'explosion': {
                        // "d8 · Exploded (link 2, needed 7+) · Fire · Frostwind Blade". The threshold
                        // is spelled out because it varies with the roller's Fortune — a player
                        // seeing a chain continue off a 7 needs to be told 7 was enough.
                        const emRec = dieMeta as Record<string, unknown>;
                        const emLink = Number(emRec.explosion_link || 0);
                        const emThresh = Number(emRec.explosion_threshold || 0);
                        const emDie = Number(emRec.explosion_die || 0);
                        let t = 'Exploded';
                        if (emLink > 0) t += ` (link ${emLink}`;
                        if (emLink > 0 && emThresh > 0) t += `, needed ${emThresh}+`;
                        if (emLink > 0) t += ')';
                        if (metaDtName !== '') t += ` · ${metaDtName}`;
                        if (metaSrcNm !== '') t += ` · ${metaSrcNm}`;
                        if (emDie > 0) t = `d${emDie} · ${t}`;
                        tooltip = t;
                        break;
                    }
                    case 'damage_mod': {
                        let t = metaDtName !== '' ? `${metaDtName} Damage Mod` : 'Damage Mod';
                        if (metaSrcNm !== '') t += ` · ${metaSrcNm}`;
                        if (originalDieForTooltip !== '') t = `${originalDieForTooltip} · ${t}`;
                        tooltip = t;
                        break;
                    }
                    default:
                        if (metaDtName !== '') {
                            let t = metaDtName;
                            if (metaSrcNm !== '') t += ` · ${metaSrcNm}`;
                            if (metaOrigin !== '' && metaOrigin !== metaSrcNm) t += ` · ${metaOrigin}`;
                            tooltip = t;
                            if (originalDieForTooltip !== '') tooltip = `${originalDieForTooltip} · ${tooltip}`;
                        }
                        break;
                }
            }

            // Damage-type segment separator
            let isPrimaryDamageDie = false;
            let thisSegmentDtId: string | null = null;
            if (dieMeta && dieMeta.damage_type_id) {
                const thisKind = String(dieMeta.kind || '');
                if (thisKind === '') {
                    isPrimaryDamageDie = true;
                    thisSegmentDtId = String(dieMeta.damage_type_id);
                }
            }
            if (isPrimaryDamageDie) {
                if (lastDamageSegmentDtId !== null && lastDamageSegmentDtId !== thisSegmentDtId) {
                    inner += ' <span class="text-muted fw-light mx-2">+</span> ';
                }
                lastDamageSegmentDtId = thisSegmentDtId;
            }

            const styleAttr = rollHexColor ? ` style="background-color:${_esc(rollHexColor)}"` : '';
            inner += `<span role="button" class="badge ${dieRollBg} d-inline-block"${styleAttr}
                data-bs-toggle="tooltip" data-bs-placement="top"
                data-bs-original-title="${_esc(tooltip)}">
                ${_esc(dieRoll)}
            </span> `;
        }

        lastBaseDie = baseDie;
    }

    // Close any open group
    if (inAdvGroup) {
        inner += `<span class='text-muted fw-light'>)</span> `;
    }

    // Modifier badge
    const rollMod = Number(roll.roll_mod || 0);
    const rollResult = Number(roll.roll_result || 0);
    if (rollMod !== 0 && showMod && rollResult !== rollMod) {
        inner += ` + <span class="badge bg-warning d-inline-block"
            data-bs-toggle="tooltip" data-bs-placement="top"
            data-bs-original-title="Mod">${_esc(rollMod)}</span>`;
    }

    // ADV/DIS trace dropdown — exclude log_banner=campaign_house_rule entries
    let advDisTrace: unknown[] | null = null;
    if (Array.isArray(advTraceRaw) && advTraceRaw.length > 0) {
        advDisTrace = advTraceRaw.filter(e =>
            e && typeof e === 'object'
            && (e as Record<string, unknown>).log_banner !== 'campaign_house_rule'
        );
    }
    if (advDisTrace && advDisTrace.length > 0) {
        inner += _renderAdvDisTrace(roll, advDisTrace);
    }

    inner += _renderGateSuppressedDamageBreakdown(roll, suppressed);

    return `<div class="row m-0"><div class="col-12 text-left">${inner}</div></div>`;
}

// ---- ADV/DIS trace block ----

const _ADV_DIS_KIND_META: Record<string, [string, string]> = {
    status_effect:   ['Status Effect',    'status_effect'],
    passive_ability: ['Passive Ability',  'passive_ability'],
    ancestry:        ['Ancestry',         'ancestry'],
    class:           ['Class Feature',    'class'],
    subclass:        ['Subclass Feature', 'subclass'],
    background:      ['Background',       'background'],
    creature_type:   ['Creature Type',    'creature_type'],
    equipment:       ['Equipment',        'inventory_equipped'],
    skill_training:  ['Skill Training',   'skill'],
    manual:          ['Manual',           'edit'],
    environment:     ['Environment',      'environment'],
    luck:            ['Luck',             'luck'],
    contesting_roll: ['Contesting Roll',  'contesting_roll'],
    condition:       ['Condition',        'hindered_attack'],
};

function _renderAdvDisTrace(roll: Roll, advDisTrace: unknown[]): string {
    const traceRollI = Number(roll.roll_i || 0);
    const traceRollIdHex = _hexOnly(roll.roll_id || '');
    let traceId: string;
    if (traceRollI > 0) {
        traceId = 'adv_dis_trace_' + traceRollI + (traceRollIdHex !== '' ? '_' + traceRollIdHex : '');
    } else if (traceRollIdHex !== '') {
        traceId = 'adv_dis_trace_' + traceRollIdHex + '_' + Math.random().toString(16).slice(2, 10);
    } else {
        traceId = 'adv_dis_trace_' + Math.random().toString(16).slice(2, 18);
    }

    const net = parseInt(String(roll.adv_dis_stacks || 0), 10);
    let traceIcon: string;
    if (net > 0) traceIcon = _icon('adv', 18, 18);
    else if (net < 0) traceIcon = _icon('dis', 18, 18);
    else traceIcon = _icon('adv_dis', 18, 18);

    let items = '';
    for (const tRaw of advDisTrace) {
        const t = tRaw as Record<string, unknown>;
        const tDelta = parseInt(String(t.delta || 0), 10);
        const tLabel = _esc(t.label || '');
        const tItemId = (t.item_id as string | undefined) || null;
        const tStatusId = (t.status_id as string | undefined) || null;
        const tLinkId = tItemId || tStatusId || null;
        const tHasLink = !!(tLinkId && _isUuid(String(tLinkId)));
        const deltaStr = tDelta > 0 ? '+' + tDelta : String(tDelta);
        const rowClass = tDelta > 0 ? 'list-group-item-success' : 'list-group-item-danger';

        const tKind = String(t.kind || '');
        const tOriginName = String(t.origin_name || '');
        const tOriginId = (t.origin_item_id as string | undefined) || null;
        const tOriginThumb = String(t.origin_thumb_url || '').trim();
        const tOriginHasLink = !!(tOriginId && _isUuid(String(tOriginId)));
        const kindMeta = _ADV_DIS_KIND_META[tKind] || null;
        const kindLabel = kindMeta ? kindMeta[0] : (tKind !== '' ? _ucwords(tKind) : '');
        const kindIconNm = kindMeta ? kindMeta[1] : null;
        const originIconSrc = tOriginId || kindIconNm;

        const linkContent = tHasLink
            ? `<span role="button" class="d-inline-flex" onclick="openItemInfo('${_esc(tLinkId)}');">${_icon(String(tLinkId), 18, 18)}</span>
               <span role="button" class="text-decoration-underline fw-semibold text-truncate" onclick="openItemInfo('${_esc(tLinkId)}');">${tLabel}</span>`
            : `<span class="fw-semibold text-truncate">${tLabel}</span>`;

        let originRow = '';
        if (tOriginName !== '') {
            // Exact inventory thumbnail when the trace carries one (equipment sources);
            // iconMap lookup is the fallback.
            const originIconHtml = tOriginThumb !== ''
                ? `<img src="${_esc(tOriginThumb)}" width="16" height="16" class="rounded flex-shrink-0" style="object-fit:cover;" alt="">`
                : (originIconSrc
                    ? `<span class="d-inline-flex flex-shrink-0" style="opacity:.85;">${_icon(String(originIconSrc), 12, 12)}</span>`
                    : '');
            const originLink = tOriginHasLink
                ? `<span role="button" class="text-decoration-underline text-truncate" onclick="openItemInfo('${_esc(tOriginId)}');">${_esc(tOriginName)}</span>`
                : `<span class="text-truncate">${_esc(tOriginName)}</span>`;
            originRow = `<div class="d-flex align-items-center gap-1 mt-1" style="font-size:10.5px; line-height:1.1; opacity:.8;">${originIconHtml}${originLink}</div>`;
        }

        let kindBadge = '';
        if (kindIconNm !== null) {
            kindBadge = `<span class="d-inline-flex flex-shrink-0 ms-auto" style="opacity:.9;" data-bs-toggle="tooltip" data-bs-placement="left" data-bs-original-title="${_esc(kindLabel)}">${_icon(kindIconNm, 20, 20)}</span>`;
        } else if (kindLabel !== '') {
            kindBadge = `<span class="badge bg-secondary ms-auto" style="font-size:9px;">${_esc(kindLabel)}</span>`;
        }

        // data-ft-adv-dis-source-* is the semantic hook the FT3D HUD log reads (ft3d-hud/project-roll.ts)
        // so it never reverse-engineers this Bootstrap layout. ⚠ Not data-ft-adv-dis-kind: that name
        // already marks a die badge's ADV/DIS tone above.
        items += `<li class="list-group-item ${rowClass} py-2 px-2 text-dark"
            data-ft-adv-dis-source data-ft-adv-dis-source-delta="${tDelta}" data-ft-adv-dis-source-label="${tLabel}"
            data-ft-adv-dis-source-item-id="${tHasLink ? _esc(tLinkId) : ''}" data-ft-adv-dis-source-kind="${_esc(tKind)}"
            data-ft-adv-dis-source-kind-label="${_esc(kindLabel)}" data-ft-adv-dis-source-kind-icon="${_esc(kindIconNm || '')}"
            data-ft-adv-dis-source-origin-name="${_esc(tOriginName)}" data-ft-adv-dis-source-origin-item-id="${tOriginHasLink ? _esc(tOriginId) : ''}"
            data-ft-adv-dis-source-origin-thumb="${_esc(tOriginThumb)}">
            <div class="d-flex align-items-center gap-2">
                <span class="badge ${tDelta > 0 ? 'bg-success' : 'bg-danger'} d-inline-flex align-items-center justify-content-center" style="min-width:34px; font-weight:700; font-size:11px;">${deltaStr}</span>
                <div class="flex-grow-1" style="min-width:0;">
                    <div class="d-flex align-items-center gap-2" style="min-width:0;">${linkContent}</div>
                    ${originRow}
                </div>
                ${kindBadge}
            </div>
        </li>`;
    }

    return `<span role="button" class="d-inline-block ms-1" data-ft-adv-dis-trace-toggle
            style="vertical-align:middle; cursor:pointer;"
            onclick='var el=document.getElementById("${_esc(traceId)}"); if(el) el.style.display = el.style.display==="none"?"block":"none";'
            data-bs-toggle="tooltip" data-bs-placement="top"
            data-bs-original-title="ADV/DIS sources — click to expand">${traceIcon}</span>
        <div id="${_esc(traceId)}" style="display:none;" class="mt-1" data-ft-adv-dis-trace data-ft-adv-dis-trace-net="${Number.isFinite(net) ? net : 0}">
            <ul class="list-group list-group-flush small" style="border:1px solid rgba(255,255,255,.15); border-radius:.375rem; overflow:hidden;">
                ${items}
            </ul>
        </div>`;
}
